import { clsx } from 'clsx';
import { useMemo } from 'react';
import { useMedia } from '#hooks/use-media';
import { MenuItem, MenuItemSpacer } from './components';
import { DualNavContext } from './contexts/dual-nav';
import styles from './dual-nav.module.css';

interface DualNavProps {
  switcherSlot?: React.ReactNode;
  mainMenuSlot?: React.ReactNode;
  footerMenuSlot?: React.ReactNode;
  logoSlot?: React.ReactNode;
  /**
   * The width state of the dual nav.
   * - collapsed: The dual nav is collapsed.
   * - expanded: The dual nav is expanded.
   * - responsive: The dual nav is responsive to the screen size and collapses when the screen is smaller than the small screen breakpoint.
   */
  widthState?: 'collapsed' | 'expanded' | 'responsive';
  className?: string;
}

function DualNav({
  switcherSlot,
  mainMenuSlot,
  footerMenuSlot,
  logoSlot,
  widthState = 'responsive',
  className,
}: DualNavProps) {
  const isSmallScreen = useMedia('sm');
  const smallScreenWidthState = isSmallScreen ? 'collapsed' : 'expanded';
  const widthStateValue = widthState === 'responsive' ? smallScreenWidthState : widthState;
  const contextValue = useMemo(() => ({ widthState: widthStateValue }), [widthStateValue]);

  const dualNavClassName = clsx(
    styles.dualNav,
    widthStateValue === 'collapsed' ? styles.collapsed : styles.expanded,
    className
  );

  return (
    <DualNavContext.Provider value={contextValue}>
      <nav className={dualNavClassName}>
        {switcherSlot ? <div className={styles.switcherSlot}>{switcherSlot}</div> : null}
        <div className={styles.mainAndFooterWrapper}>
          <ul className={styles.mainMenuSlot} role={mainMenuSlot ? undefined : 'presentation'}>
            {mainMenuSlot}
          </ul>
          {footerMenuSlot ? <ul className={styles.footerMenuSlot}>{footerMenuSlot}</ul> : null}
        </div>
        {logoSlot ? <div className={styles.logoSlot}>{logoSlot}</div> : null}
        {/* TODO Submenu: <div className={styles.overlay} /> */}
      </nav>
    </DualNavContext.Provider>
  );
}

DualNav.MenuItem = MenuItem;
DualNav.MenuItemSpacer = MenuItemSpacer;

export { DualNav };
